<script setup lang="ts">
import BaseLayout from "@/layouts/BaseLayout.vue";
import {router, useForm} from "@inertiajs/vue3";
import {route} from "ziggy-js";
import VFieldLabel from "@/components/forms/VFieldLabel.vue";
import {ref} from "vue";
import VEditor from "@/components/forms/VEditor.vue";
import {Topic} from "@/types";
import {useColors} from "@/composables/colors";

defineOptions({layout: BaseLayout})

const {randomColor} = useColors()

interface Props {
    title: string
    topics: Array<Topic>
}

const props = defineProps<Props>();

const form = useForm({
    name: '',
    description: '',
    topics: [] as Array<Topic>
})

const search = ref<string>('')
const isBeingEdited = ref<boolean>(false)

const toggleEdit = () => {
    isBeingEdited.value = !isBeingEdited.value;
}

const closeEdit = () => {
    isBeingEdited.value = false;
}

const removeTopic = (topic: Topic) => {
    form.topics = form.topics.filter(t => t.id !== topic.id);
}

const submit = () => {
    form.post(route('capacity-frameworks.store'))
}

const goBack = () => {
    router.get(route('capacity-frameworks.index'))
}
</script>

<template>
    <v-container>
        <v-row class="align-center justify-space-between px-3">
            <h1 class="text-h4 font-weight-bold">
                {{ props.title }}
            </h1>
        </v-row>
        <v-form
            @submit.prevent="submit"
        >
            <v-row
                class="mt-5"
            >
                <v-col
                    cols="12"
                    class="py-0"
                >
                    <v-field-label>
                        Name (required)
                    </v-field-label>
                    <v-text-field
                        v-model="form.name"
                        type="text"
                        name="name"
                        class="mb-3"
                        density="compact"
                        :hide-details="!form.errors.name"
                        :error-messages="form.errors.name"
                        autofocus
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="8"
                    class="py-0"
                >
                    <v-field-label>
                        Description
                    </v-field-label>
                    <v-editor
                        v-model="form.description"
                        placeholder="Describe your framework"
                    />
                </v-col>
                <v-col
                    cols="12"
                    md="4"
                >
                    <v-list class="pa-0">
                        <v-list-item>
                            <v-list-item-title class="text-body-2 text-black font-weight-bold">
                                Labels
                                <v-btn
                                    color="black"
                                    variant="text"
                                    size="x-small"
                                    class="float-end py-1 px-2"
                                    @click="toggleEdit"
                                >
                                    <span class="font-weight-regular">
                                        {{ isBeingEdited ? 'Apply' : 'Edit' }}
                                    </span>
                                </v-btn>
                            </v-list-item-title>
                            <v-list-item-subtitle
                                v-if="!isBeingEdited"
                                class="d-block mt-1"
                            >
                                <span
                                    v-if="form.topics.length === 0"
                                    class="text-body-2"
                                >
                                    None
                                </span>
                                <v-chip
                                    v-for="topic in form.topics"
                                    :key="topic.id"
                                    variant="flat"
                                    class="mx-1 mb-1 text-label"
                                    size="x-small"
                                    :color="randomColor(topic.id)"
                                >
                                    {{ topic.name }}
                                    <template #append>
                                        <v-icon-btn
                                            variant="text"
                                            icon="mdi-close"
                                            size="small"
                                            icon-size="small"
                                            class="ml-1"
                                            @click.stop="removeTopic(topic)"
                                        />
                                    </template>
                                </v-chip>
                            </v-list-item-subtitle>
                            <v-list-item-subtitle
                                v-if="isBeingEdited"
                                class="d-flex mt-1"
                            >
                                <v-autocomplete
                                    v-if="isBeingEdited"
                                    v-model="form.topics"
                                    v-model:search="search"
                                    variant="outlined"
                                    :items="props.topics"
                                    density="compact"
                                    clear-on-select
                                    return-object
                                    multiple
                                    hide-details
                                    item-title="name"
                                    item-value="id"
                                    no-data-text="No labels found"
                                    :list-props="{
                                        class: 'py-0'
                                    }"
                                    :menu-props="{
                                        location: 'bottom'
                                    }"
                                    menu-icon="null"
                                    @blur="closeEdit"
                                >
                                    <template #item="{ item, props: binds }">
                                        <v-list-item
                                            :value="binds.value"
                                            link
                                            @click="binds.onClick as Event"
                                        >
                                            <v-list-item-title
                                                class="text-body-2 font-weight-bold"
                                            >
                                                <v-badge
                                                    variant="flat"
                                                    :color="randomColor(item.raw.id)"
                                                    height="8"
                                                    width="6"
                                                    class="mb-3 mx-3"
                                                />
                                                {{ item.raw.name }}
                                            </v-list-item-title>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>
                            </v-list-item-subtitle>
                        </v-list-item>
                    </v-list>
                </v-col>
            </v-row>
            <v-row>
                <v-col
                    cols="12"
                >
                    <v-btn
                        color="primary"
                        type="submit"
                    >
                        Create framework
                    </v-btn>
                    <v-btn
                        color="black"
                        variant="text"
                        border="sm"
                        class="ml-2"
                        @click="goBack"
                    >
                        <span class="font-weight-regular">
                            Cancel
                        </span>
                    </v-btn>
                </v-col>
            </v-row>
        </v-form>
    </v-container>
</template>

<style scoped>
:deep(.v-list-item__prepend) {
    width: 16px;
}

.text-label {
    font-size: 0.75rem;
}
</style>
